import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import {
  closeDuplicatedTaxPaymentModal,
  fetchSender,
  setFieldsValues,
  setPrefilledRejectOrConfirm,
  setTermsErrors,
  updateOrderWithEntities,
  validateSections,
  validateSenderAddress,
} from 'actions';
import { Info } from 'components/Alert/Info/Info';
import { Fetching } from 'components/Fetching/Fetching';
import {
  WithFocusOnError,
  type WithFocusOnErrorProps,
} from 'components/HOC/WithFocusOnError/WithFocusOnError';
import { Navigation } from 'components/Navigation/Navigation';
import { PaymentStep } from 'components/PaymentStep/PaymentStep';
import { RequiredFieldLabel } from 'components/RequiredFieldLabel/RequiredFieldLabel';
import { Section } from 'components/SectionList/Section/Section';
import {
  CHINA_PAYMENT_OPTIONS_WITH_CUSTOM_SECTION,
  INDIA_PAYMENT_OPTIONS_WITH_CUSTOM_SECTION,
  INTERAC_PAYMENT_OPTION_WITH_CUSTOM_SECTION,
  OFFER_ENTITY,
  PAYER_FORM,
  SENDER_ENTITY,
  TERMS_ACCEPTED_FIELD,
} from 'constants/index';
import { TaxModalContainer as TaxModal } from 'containers/Modals/TaxModal/TaxModal';
import type { RootState } from 'reducers/types';
import {
  getFieldValue,
  getInvalidParamErrors,
  getLastPaymentFromSameRecipient,
  getNonPrefillableFields,
  getRecipient,
  getSectionsByEntities,
  getSelectedOffer,
  hasGatewayOrigin,
  isDuplicatedTaxPaymentModalOpen,
  isEmbedded,
  shouldOpenTaxModal,
  shouldShowCashback,
  shouldShowPrefillForm,
  sourceOfFundsIndia,
} from 'selectors';
import { useTranslations } from 'utils';

import { ChinaOfferSection } from './components/ChinaOfferSection/ChinaOfferSection';
import { DuplicatedTaxPaymentModal } from './components/DuplicatedTaxPaymentModal/DuplicatedTaxPaymentModal';
import { IndiaOfferSection } from './components/IndiaOfferSection/IndiaOfferSection';
import { InteracOfferSection } from './components/InteracOfferSection/InteracOfferSection';
import { NextStep } from './components/NextStep/NextStep';
import { PrefillModal } from './components/PrefillModal/PrefillModal';
import { PrevStep } from './components/PrevStep/PrevStep';
import { SenderInformationAcceptGroup } from './components/SenderInformationAcceptGroup/SenderInformationAcceptGroup';
import { useDispatch } from 'store/configureStore';
import type { Field } from 'models/field';
import type { Section as SectionModel, SectionField } from 'models/section';
import { MandatoryAccount } from './components/MandatoryAccount/MandatoryAccount';

const SenderInformationComponent = ({
  focusOnFirstError,
}: WithFocusOnErrorProps) => {
  const dispatch = useDispatch();
  const i18n = useTranslations();

  const areTermsAccepted = useSelector(
    (state: RootState) => !!getFieldValue(state, TERMS_ACCEPTED_FIELD),
  );
  const errors = useSelector(getInvalidParamErrors);
  const _hasGatewayOrigin = useSelector(hasGatewayOrigin);
  const _isDuplicatedTaxPaymentModalOpen = useSelector(
    isDuplicatedTaxPaymentModalOpen,
  );
  const _isEmbedded = useSelector(isEmbedded);
  const isPrefillModalOpen = useSelector(shouldShowPrefillForm);

  const _shouldOpenTaxModal = useSelector(shouldOpenTaxModal);
  const lastPaymentFromSameRecipient = useSelector(
    getLastPaymentFromSameRecipient,
  );
  const nonPrefillableFields = useSelector(getNonPrefillableFields);
  const recipient = useSelector(getRecipient);
  const senderSection = useSelector((state: RootState) =>
    getSectionsByEntities(state, [SENDER_ENTITY]),
  )?.[0];
  const offerSection = useSelector((state: RootState) =>
    getSectionsByEntities(state, [OFFER_ENTITY]),
  )?.[0];
  const sections = {
    ...(senderSection && { [SENDER_ENTITY]: senderSection }),
    ...(offerSection && { [OFFER_ENTITY]: offerSection }),
  };

  const [goToNextStep, setGoToNextStep] = useState(false);
  const [goToPrevStep, setGoToPrevStep] = useState(false);
  const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);

  // This is needed if we need to check the latest value of shouldOpenTaxModal in the handleClickNext function
  const shouldOpenTaxModalRef = useRef(_shouldOpenTaxModal);

  useEffect(() => {
    shouldOpenTaxModalRef.current = _shouldOpenTaxModal;
  }, [_shouldOpenTaxModal]);

  const handleClickPrev = () => {
    setGoToPrevStep(true);
  };

  const handleClickNext = async () => {
    if (await validate()) {
      dispatch(setPrefilledRejectOrConfirm(true));
      await dispatch(updateOrderWithEntities([SENDER_ENTITY, OFFER_ENTITY]))
        .then(() => {
          if (shouldOpenTaxModalRef.current) {
            setIsTaxModalOpen(true);
          } else {
            setGoToNextStep(true);
          }
        })
        .catch(() => {
          validate();
          focusOnFirstError();
        });
    } else {
      focusOnFirstError();
    }
  };

  const handleOnCloseTaxModal = async () => {
    setIsTaxModalOpen(false);
    setGoToNextStep(true);
  };

  const validate = async () => {
    const isSectionValid = await dispatch(
      validateSections(
        Object.keys(sections).map(id => sections[id as keyof typeof sections]),
        true,
      ),
    );
    const areTermsValid = validateTerms();
    const isSenderAddressValid = await dispatch(validateSenderAddress());

    return isSectionValid && areTermsValid && isSenderAddressValid;
  };

  const validateTerms = () => {
    if (!areTermsAccepted) {
      dispatch(setTermsErrors());
    }
    return areTermsAccepted;
  };

  const prefillForm = () => {
    const offerFields = lastPaymentFromSameRecipient?.offer.fields as Field[];
    const recipientFields = lastPaymentFromSameRecipient?.recipient.fields;
    const senderFields = lastPaymentFromSameRecipient?.sender.fields;

    const offerValues = offerFields?.reduce((acc, value) => {
      return { ...acc, [`offer_${value.id}`]: value.value };
    }, {});

    const recipientValues = recipientFields?.reduce((acc, value) => {
      return { ...acc, [value.id]: value.value };
    }, {});

    const senderValues = senderFields?.reduce((acc, value) => {
      if (value.id === 'country') {
        return acc;
      }

      return { ...acc, [`sender_${value.id}`]: value.value };
    }, {});

    let fieldValues = { ...offerValues, ...recipientValues, ...senderValues };

    nonPrefillableFields.forEach(id => {
      const { [id]: found, ...rest }: Record<string, unknown> = fieldValues;
      fieldValues = rest;
    });

    dispatch(setFieldsValues(fieldValues));

    closeModal();
  };

  const closeModal = () => {
    dispatch(setPrefilledRejectOrConfirm(true));
  };

  const confirmationBody = useCallback(() => {
    return _hasGatewayOrigin
      ? i18n.t('senderInformation.prefillFormBody.integrated', {
          institution: recipient.name as string,
        })
      : i18n.t('senderInformation.prefillFormBody');
  }, [_hasGatewayOrigin, recipient.name, i18n]);

  if (goToPrevStep) {
    return <PrevStep />;
  }

  if (!shouldOpenTaxModalRef.current && goToNextStep) {
    return <NextStep />;
  }

  return (
    <>
      {isPrefillModalOpen && (
        <PrefillModal
          title={i18n.t('senderInformation.prefillFormTitle', {
            institution: recipient.name as string,
          })}
          body={confirmationBody()}
          isOpen
          onClose={closeModal}
          onConfirm={prefillForm}
          onReject={closeModal}
        />
      )}
      <TaxModal isOpen={isTaxModalOpen} onClose={handleOnCloseTaxModal} />
      <DuplicatedTaxPaymentModal
        isOpen={_isDuplicatedTaxPaymentModalOpen}
        onClose={() => dispatch(closeDuplicatedTaxPaymentModal())}
      />
      <Fetching entity="order" />
      <PaymentStep
        navigation={
          <Navigation
            onClickPrev={handleClickPrev}
            onClickNext={handleClickNext}
            errors={errors}
            previousStepName={i18n.t('stepsList.offers')}
            nextStepName={i18n.t('stepsList.recipient')}
          />
        }
        stepName={PAYER_FORM}
        title={i18n.t('senderInformation.title') as string}
      >
        <Info className="flex justify-center">
          <Info.FilledIcon height="14" width="14" className="marginRight-sm" />
          {i18n.t('senderInformation.infoAlert')}
        </Info>
        <RequiredFieldLabel />
        <SenderFields sections={sections} />
        {!_isEmbedded && <SenderInformationAcceptGroup />}
      </PaymentStep>
      <MandatoryAccount />
    </>
  );
};

const SenderInformation = WithFocusOnError(SenderInformationComponent);

const SenderFields = ({
  sections,
}: {
  sections: {
    offers: SectionModel<SectionField>;
    sender: SectionModel<SectionField>;
  };
}) => {
  const { paymentOption } = useSelector(getSelectedOffer);
  const sourceOfFunds = useSelector(sourceOfFundsIndia);
  const i18n = useTranslations();
  const dispatch = useDispatch();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    dispatch(fetchSender());
  }, [i18n.currentLocale]);

  return Object.keys(sections).map(id => {
    const { name, ...sectionWithoutName } =
      sections[id as keyof typeof sections];

    if (
      id === OFFER_ENTITY &&
      INDIA_PAYMENT_OPTIONS_WITH_CUSTOM_SECTION.includes(paymentOption)
    ) {
      return (
        <IndiaOfferSection
          key={id}
          paymentOption={paymentOption}
          sourceOfFunds={sourceOfFunds as string}
        >
          <Section {...sectionWithoutName} />
        </IndiaOfferSection>
      );
    }

    if (
      id === OFFER_ENTITY &&
      CHINA_PAYMENT_OPTIONS_WITH_CUSTOM_SECTION.includes(paymentOption)
    ) {
      return (
        <ChinaOfferSection key={id} i18n={i18n}>
          <Section {...sectionWithoutName} />
        </ChinaOfferSection>
      );
    }

    if (
      id === OFFER_ENTITY &&
      paymentOption?.includes(INTERAC_PAYMENT_OPTION_WITH_CUSTOM_SECTION)
    ) {
      return (
        <InteracOfferSection key={id} i18n={i18n}>
          <Section {...sectionWithoutName} />
        </InteracOfferSection>
      );
    }

    return <Section key={id} {...sectionWithoutName} />;
  });
};

export { SenderInformation };
